import { useContext, useRef, useState } from 'react';
import { useConfigurableHotkey } from '@/hooks/useConfigurableHotkey';
import { HOTKEY_IDS } from '@/lib/hotkeys/registry';
import { AuthContext } from '@/context/auth.context';
import { SendFeedbackMenuButton } from './SendFeedbackMenuButton';
import {
  buildUiBugReportContext,
  useSubmitUiBugReport,
} from '@/api/rpc/hooks/core/ui-bug-report/useSubmitUiBugReport';
import { captureViewportScreenshot } from '@/components/feedback-panel/captureViewportScreenshot';
import { blobToDataUrl } from '@/lib/blobToDataUrl';
import {
  LegacyFeedbackDialog,
  type LegacyScreenshotState,
} from './LegacyFeedbackDialog';

type DialogState = 'idle' | 'open' | 'submitting';

async function captureAsDataUrl(): Promise<LegacyScreenshotState> {
  const result = await captureViewportScreenshot();
  if (!result.ok) return { status: 'failed', error: result.error };
  try {
    return { status: 'done', dataUrl: await blobToDataUrl(result.blob) };
  } catch (err) {
    return {
      status: 'failed',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * The pre-flag "Send feedback" entry: a modal with a textarea and one
 * auto-captured screenshot, emailed to support via `core.uiBugReport.submit`
 * and stored nowhere. Used while the `feedback-triage-enabled` flag is off.
 */
export function LegacyFeedbackButton() {
  const { currentUser } = useContext(AuthContext);
  const { mutateAsync: submitReport, isPending } = useSubmitUiBugReport();

  const [dialogState, setDialogState] = useState<DialogState>('idle');
  const [screenshot, setScreenshot] = useState<LegacyScreenshotState>({
    status: 'capturing',
  });
  const [message, setMessage] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isDialogOpen = dialogState !== 'idle';
  const isSubmitting = dialogState === 'submitting';

  // react-hotkeys-hook skips key events from inputs/textareas by default, so
  // this won't fire while the user is typing in a field.
  useConfigurableHotkey(
    HOTKEY_IDS.feedback,
    () => handleOpen(),
    { preventDefault: true },
    [currentUser, isDialogOpen],
  );

  function reset() {
    setDialogState('idle');
    setScreenshot({ status: 'capturing' });
    setMessage('');
    setIncludeScreenshot(true);
  }

  function handleOpen() {
    if (!currentUser || isDialogOpen) return;
    setScreenshot({ status: 'capturing' });
    setMessage('');
    setIncludeScreenshot(true);
    setDialogState('open');
    setTimeout(() => textareaRef.current?.focus(), 50);
    // Capture after the dialog has painted so it is excluded from the shot.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void captureAsDataUrl().then(setScreenshot);
      });
    });
  }

  async function handleSubmit() {
    if (!currentUser || dialogState !== 'open' || !message.trim()) return;
    setDialogState('submitting');
    try {
      await submitReport({
        message: message.trim(),
        screenshotDataUrl:
          includeScreenshot && screenshot.status === 'done'
            ? screenshot.dataUrl
            : null,
        context: buildUiBugReportContext(currentUser),
      });
      reset();
    } catch {
      // Error already handled by the mutation hook toast
      setDialogState('open');
    }
  }

  if (!currentUser) return null;

  return (
    <>
      <SendFeedbackMenuButton
        onClick={handleOpen}
        disabled={isDialogOpen || isPending}
      />

      <LegacyFeedbackDialog
        open={isDialogOpen}
        submitting={isSubmitting}
        screenshot={screenshot}
        includeScreenshot={includeScreenshot}
        onIncludeScreenshotChange={setIncludeScreenshot}
        message={message}
        onMessageChange={setMessage}
        onClose={() => {
          if (!isSubmitting) reset();
        }}
        onSubmit={() => void handleSubmit()}
        textareaRef={textareaRef}
      />
    </>
  );
}
